import { useEffect, useState } from "react";
import { BadgeCheck, Info } from "lucide-react";
import { api } from "@/lib/api";
import { Stars } from "./ProductCard";

const Select = ({ value, onChange, options, label, testid }) => (
  <select data-testid={testid} aria-label={label} value={value} onChange={(e) => onChange(e.target.value)} className="rounded-full border border-[#E7E2DB] bg-white px-4 py-2 text-sm outline-none focus:border-[#B85D43]">
    <option value="">{label}</option>
    {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

export const ReviewCard = ({ r }) => (
  <div data-testid="review-card" className="break-inside-avoid rounded-2xl border border-[#E7E2DB] bg-white p-5">
    {r.photo && <img src={r.photo} alt={`Customer photo wearing ${r.product_name}`} loading="lazy" className="mb-4 aspect-[4/5] w-full rounded-xl object-cover object-top" />}
    <div className="flex items-center justify-between"><Stars value={r.rating} /><span className="text-[11px] text-[#A8A29E]">{new Date(r.created_at).toLocaleDateString("en-IN", { month: "short", year: "numeric" })}</span></div>
    <p className="mt-3 font-serif-display text-xl font-semibold leading-snug">{r.title}</p>
    <p className="mt-1.5 text-sm leading-relaxed text-[#57534E]">{r.text}</p>
    <div className="mt-4 flex flex-wrap gap-1.5 text-[11px]">
      <span className="rounded-full bg-[#F4EFEA] px-2.5 py-1">Size {r.size}</span>
      <span className="rounded-full bg-[#F4EFEA] px-2.5 py-1">{r.fit}</span>
    </div>
    <div className="mt-4 flex items-center justify-between border-t border-[#E7E2DB] pt-3 text-xs">
      <span className="font-medium">{r.name} · <span className="text-[#78716C]">{r.city}</span></span>
      {r.verified ? (
        <span className="flex items-center gap-1 font-semibold text-[#1B4332]"><BadgeCheck className="h-3.5 w-3.5" /> Verified Purchase</span>
      ) : r.sample ? (
        <span data-testid="review-sample-label" className="rounded-full border border-dashed border-[#B85D43] px-2 py-0.5 text-[10px] uppercase tracking-wider text-[#B85D43]">Sample review</span>
      ) : null}
    </div>
    <p className="mt-2 text-[11px] text-[#A8A29E]">on {r.product_name}</p>
  </div>
);

export default function ReviewsSection({ product, title = "Loved by Women Across India", products = [] }) {
  const [data, setData] = useState(null);
  const [f, setF] = useState({ product: product || "", size: "", fit: "" });

  useEffect(() => {
    api.get("/reviews", { params: Object.fromEntries(Object.entries(f).filter(([, v]) => v)) }).then((r) => setData(r.data));
  }, [f]);
  if (!data) return null;

  return (
    <section data-testid="reviews-section" className="kt-container py-16 sm:py-24">
      <div className="grid gap-10 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <p className="eyebrow">Customer love</p>
          <h2 className="mt-3 font-serif-display text-4xl font-semibold leading-tight sm:text-5xl">{title}</h2>
          <div className="mt-8 flex items-end gap-4">
            <span data-testid="reviews-average" className="font-serif-display text-7xl font-semibold text-[#701A24]">{data.average || "–"}</span>
            <div className="pb-3"><Stars value={data.average} size="h-5 w-5" /><p data-testid="reviews-total" className="mt-1 text-sm text-[#57534E]">Based on {data.total} reviews</p></div>
          </div>
          <div className="mt-6 space-y-2">
            {[5, 4, 3, 2, 1].map((s) => {
              const n = data.distribution[s] || 0;
              return (
                <div key={s} className="flex items-center gap-3 text-xs">
                  <span className="w-3">{s}</span>
                  <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[#EAE0D5]"><div className="h-full rounded-full bg-[#B85D43]" style={{ width: `${data.total ? (n / data.total) * 100 : 0}%` }} /></div>
                  <span className="w-5 text-right text-[#78716C]">{n}</span>
                </div>
              );
            })}
          </div>
          <p className="mt-6 flex gap-2 rounded-xl bg-[#F4EFEA] p-3 text-[11px] leading-relaxed text-[#57534E]"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Reviews tagged “Sample” are launch placeholders and will be replaced by real customer reviews. Only reviews linked to a real order show “Verified Purchase”.</p>
        </div>
        <div className="lg:col-span-8">
          <div className="mb-6 flex flex-wrap gap-2">
            {!product && <Select testid="review-filter-product" label="All products" value={f.product} onChange={(v) => setF({ ...f, product: v })} options={products.map((p) => [p.slug, p.name])} />}
            <Select testid="review-filter-size" label="All sizes" value={f.size} onChange={(v) => setF({ ...f, size: v })} options={["XS", "S", "M", "L", "XL", "XXL"].map((s) => [s, `Size ${s}`])} />
            <Select testid="review-filter-fit" label="Any fit" value={f.fit} onChange={(v) => setF({ ...f, fit: v })} options={["True to size", "Runs small", "Runs large"].map((s) => [s, s])} />
          </div>
          {data.reviews.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-[#E7E2DB] p-10 text-center text-sm text-[#78716C]">No reviews match these filters yet.</p>
          ) : (
            <div className="columns-1 gap-5 space-y-5 sm:columns-2">
              {data.reviews.slice(0, product ? 20 : 8).map((r) => <ReviewCard key={r.id} r={r} />)}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
